import { AUTH_ERROR, AUTH_EVENT, AUTH_STATE } from "./authenticationConstants";

/** State transitions for the treatment authentication boundary. */

const TRANSITIONS = Object.freeze({
  [AUTH_STATE.unidentified]: {
    [AUTH_EVENT.submitEmail]: AUTH_STATE.lookupPending,
  },
  [AUTH_STATE.lookupPending]: {
    [AUTH_EVENT.lookupFound]: AUTH_STATE.existingCredentials,
    [AUTH_EVENT.lookupNotFound]: AUTH_STATE.newRegistration,
    [AUTH_EVENT.lookupInactive]: AUTH_STATE.inactiveAccount,
    [AUTH_EVENT.lookupNonPatient]: AUTH_STATE.existingNonPatient,
    [AUTH_EVENT.lookupUnavailable]: AUTH_STATE.lookupRetry,
  },
  [AUTH_STATE.lookupRetry]: {
    [AUTH_EVENT.retry]: AUTH_STATE.lookupPending,
    [AUTH_EVENT.changeEmail]: AUTH_STATE.unidentified,
  },
  [AUTH_STATE.existingCredentials]: {
    [AUTH_EVENT.submitCredentials]: AUTH_STATE.existingCredentials,
    [AUTH_EVENT.credentialsAccepted]: AUTH_STATE.authenticated,
    [AUTH_EVENT.credentialsRejected]: AUTH_STATE.existingCredentials,
    [AUTH_EVENT.changeEmail]: AUTH_STATE.unidentified,
    [AUTH_EVENT.forgotPassword]: AUTH_STATE.passwordReset,
  },
  // The reset steps (email, code, new password) live in PasswordResetStep;
  // after a reset the patient signs in through the existing-credentials path.
  [AUTH_STATE.passwordReset]: {
    [AUTH_EVENT.backToLogin]: AUTH_STATE.existingCredentials,
    [AUTH_EVENT.changeEmail]: AUTH_STATE.unidentified,
  },
  [AUTH_STATE.inactiveAccount]: {
    [AUTH_EVENT.changeEmail]: AUTH_STATE.unidentified,
  },
  [AUTH_STATE.existingNonPatient]: {
    [AUTH_EVENT.changeEmail]: AUTH_STATE.unidentified,
  },
  [AUTH_STATE.newRegistration]: {
    [AUTH_EVENT.submitRegistration]: AUTH_STATE.newRegistration,
    [AUTH_EVENT.accountCreated]: AUTH_STATE.authenticated,
    [AUTH_EVENT.identityExists]: AUTH_STATE.existingCredentials,
    [AUTH_EVENT.validationFailed]: AUTH_STATE.newRegistration,
    [AUTH_EVENT.changeEmail]: AUTH_STATE.unidentified,
  },
  [AUTH_STATE.authenticated]: {
    [AUTH_EVENT.bindSucceeded]: AUTH_STATE.intakeReady,
    [AUTH_EVENT.bindDisqualified]: AUTH_STATE.eligibilityBlocked,
    [AUTH_EVENT.sessionExpired]: AUTH_STATE.existingCredentials,
    [AUTH_EVENT.changeEmail]: AUTH_STATE.unidentified,
    [AUTH_EVENT.resetIdentity]: AUTH_STATE.reset,
  },
  [AUTH_STATE.intakeReady]: {
    [AUTH_EVENT.submitProfile]: AUTH_STATE.intakeReady,
    [AUTH_EVENT.bindSucceeded]: AUTH_STATE.intakeReady,
    [AUTH_EVENT.bindDisqualified]: AUTH_STATE.eligibilityBlocked,
    [AUTH_EVENT.validationFailed]: AUTH_STATE.intakeReady,
    [AUTH_EVENT.sessionExpired]: AUTH_STATE.existingCredentials,
    [AUTH_EVENT.changeEmail]: AUTH_STATE.unidentified,
    [AUTH_EVENT.resetIdentity]: AUTH_STATE.reset,
  },
  [AUTH_STATE.eligibilityBlocked]: {
    [AUTH_EVENT.changeEmail]: AUTH_STATE.unidentified,
    [AUTH_EVENT.resetIdentity]: AUTH_STATE.reset,
  },
  [AUTH_STATE.reset]: {
    [AUTH_EVENT.changeEmail]: AUTH_STATE.unidentified,
    [AUTH_EVENT.resetIdentity]: AUTH_STATE.unidentified,
  },
});

/** Identity data that must not survive an email or identity change (C3). */
const CLEARED_ON_RESET = Object.freeze({
  patient: null,
  profile: null,
  accessToken: null,
  missingFields: [],
});

export const initialAuthenticationContext = Object.freeze({
  state: AUTH_STATE.unidentified,
  email: "",
  error: null,
  errorMessage: null,
  pending: false,
  ...CLEARED_ON_RESET,
});

export const canTransition = (state, event) =>
  Boolean(TRANSITIONS[state]?.[event]);

export const nextAuthenticationState = (state, event) =>
  TRANSITIONS[state]?.[event] || null;

/**
 * Apply an event to the machine context.
 *
 * Unknown transitions are ignored rather than throwing: a late response from a
 * superseded request (for example, a lookup that resolves after the patient has
 * already gone back) must not corrupt the current branch.
 */
export const authenticationReducer = (context, event) => {
  const target = nextAuthenticationState(context.state, event.type);
  if (!target) return context;

  const base = {
    ...context,
    state: target,
    pending: false,
    error: null,
    errorMessage: null,
  };

  switch (event.type) {
    case AUTH_EVENT.submitEmail:
      return { ...base, email: event.email, pending: true };
    case AUTH_EVENT.submitCredentials:
    case AUTH_EVENT.submitRegistration:
    case AUTH_EVENT.submitProfile:
      return { ...base, pending: true, fieldErrors: null };
    case AUTH_EVENT.retry:
      return { ...base, pending: true };
    case AUTH_EVENT.lookupUnavailable:
      return { ...base, error: AUTH_ERROR.emailLookupUnavailable };
    case AUTH_EVENT.lookupInactive:
      return { ...base, error: AUTH_ERROR.inactiveAccount };
    case AUTH_EVENT.lookupNonPatient:
      return { ...base, error: AUTH_ERROR.nonPatientAccount };
    case AUTH_EVENT.credentialsRejected:
      return {
        ...base,
        error: event.error || AUTH_ERROR.credentialsInvalid,
        errorMessage: event.errorMessage || null,
      };
    case AUTH_EVENT.identityExists:
      return {
        ...base,
        error: event.error || AUTH_ERROR.identityConflict,
      };
    case AUTH_EVENT.validationFailed:
      return { ...base, error: event.error || null, fieldErrors: event.fieldErrors };
    case AUTH_EVENT.credentialsAccepted:
    case AUTH_EVENT.accountCreated:
      return {
        ...base,
        patient: event.patient || null,
        accessToken: event.accessToken || null,
        profile: event.profile || null,
        missingFields: event.profile?.missing_fields || [],
      };
    case AUTH_EVENT.bindSucceeded:
    case AUTH_EVENT.bindDisqualified:
      return {
        ...base,
        profile: event.profile || context.profile,
        missingFields:
          event.profile?.missing_fields || context.missingFields || [],
      };
    case AUTH_EVENT.sessionExpired:
      return { ...base, ...CLEARED_ON_RESET, email: context.email };
    case AUTH_EVENT.changeEmail:
    case AUTH_EVENT.resetIdentity:
      return { ...base, ...CLEARED_ON_RESET, email: "" };
    default:
      return base;
  }
};

/** True once the runtime session is bound and clinical questions may render. */
export const isIntakeReady = (context) =>
  context.state === AUTH_STATE.intakeReady;

/** True when the backend reported eligibility fields still missing (C5). */
export const requiresProfileCompletion = (context) =>
  Boolean(context.missingFields && context.missingFields.length > 0);
